import React, { useCallback, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { notify } from "./NotificationsPortal";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import QRCode from "qrcode";
import { Lock } from "lucide-react";
import { notifyMfaEmail, getDeviceInfo } from "../utils/mfaNotify";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "560px",
    borderRadius: "22px",
    padding: "0",
    border: "1px solid #eef2f7",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
    overflow: "hidden",
    background: "#ffffff",
    fontFamily: "Inter, sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    zIndex: 2000,
  },
};

export default function TwoFATotpCard({ onDone }) {
  const { user } = useAuth();
  const secretRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [selectedApp, setSelectedApp] = useState("Google Authenticator");
  const [showManual, setShowManual] = useState(false);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);
  const code = useMemo(() => digits.join(""), [digits]);
  const canSubmit = useMemo(() => /^\d{6}$/.test(code), [code]);

  const resetModal = useCallback(() => {
    setQrDataUrl("");
    setManualKey("");
    setSelectedApp("Google Authenticator");
    setShowManual(false);
    setDigits(["", "", "", "", "", ""]);
    secretRef.current = null;
    inputsRef.current = [];
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setStepLoading(false);
    resetModal();
  }, [resetModal]);

  const openAndPrepare = useCallback(async () => {
    try {
      if (!user) return notify("error", "No hay sesión activa.");

      setLoading(true);
      setStepLoading(true);

      await user.reload();

      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);

      const issuer = "ComercialApp";
      const accountName = user.email || "usuario";
      const otpauthUrl = await secret.generateQrCodeUrl(issuer, accountName);

      const qr = await QRCode.toDataURL(otpauthUrl, {
        width: 240,
        margin: 1,
        errorCorrectionLevel: "M",
      });

      secretRef.current = secret;
      setQrDataUrl(qr);
      setManualKey(secret.secretKey);

      setIsOpen(true);

      // focus primer input al abrir
      setTimeout(() => inputsRef.current?.[0]?.focus?.(), 120);
    } catch (e) {
      console.error("[TOTP prepare] error:", e?.code, e?.message);
      notify("error", e?.message || "No se pudo iniciar la activación 2FA.");
      resetModal();
      setIsOpen(false);
    } finally {
      setStepLoading(false);
      setLoading(false);
    }
  }, [user, resetModal]);

  const handleDigitChange = useCallback((idx, raw) => {
    const v = (raw || "").replace(/\D/g, "").slice(-1); // 1 dígito
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });

    if (v && idx < 5) {
      inputsRef.current?.[idx + 1]?.focus?.();
    }
  }, []);

  const handleKeyDown = useCallback(
    (idx, e) => {
      if (e.key === "Backspace") {
        if (digits[idx]) {
          // borra el actual
          setDigits((prev) => {
            const next = [...prev];
            next[idx] = "";
            return next;
          });
          return;
        }
        // si está vacío, regresa
        if (idx > 0) inputsRef.current?.[idx - 1]?.focus?.();
      }

      if (e.key === "ArrowLeft" && idx > 0)
        inputsRef.current?.[idx - 1]?.focus?.();
      if (e.key === "ArrowRight" && idx < 5)
        inputsRef.current?.[idx + 1]?.focus?.();
    },
    [digits],
  );

  const handlePaste = useCallback((e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;

    const six = text.slice(0, 6).split("");
    if (six.length) {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        for (let i = 0; i < 6; i++) next[i] = six[i] || "";
        return next;
      });
      const focusIndex = Math.min(5, six.length - 1);
      setTimeout(() => inputsRef.current?.[focusIndex]?.focus?.(), 0);
    }
  }, []);

  const confirmEnroll = useCallback(async () => {
    try {
      if (!user) return notify("error", "No hay sesión activa.");
      if (!secretRef.current) return notify("error", "Sesión TOTP inválida.");

      if (!canSubmit) return;

      setStepLoading(true);

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secretRef.current,
        code,
      );

      await multiFactor(user).enroll(assertion, selectedApp);

      await setDoc(
        doc(db, "users", user.uid, "security", "mfa"),
        {
          enabled: true,
          method: "totp",
          label: selectedApp, // Google Authenticator / Authy
          updatedAt: serverTimestamp(),
          // opcional: te sirve para auditoría
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );

      try {
        await notifyMfaEmail({
          action: "enabled",
          factor: "totp",
          factorLabel: selectedApp, // "Google Authenticator" / "Authy"
          device: getDeviceInfo(),
        });
      } catch (err) {
        console.warn(
          "[MFA notify email] no se pudo enviar:",
          err?.message || err,
        );
      }

      closeModal();

      await Swal.fire({
        icon: "success",
        title: "¡Activada con aplicación!",
        text: "La autenticación de dos factores con aplicación ha sido activada exitosamente.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#16a34a",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      notify("success", "2FA por aplicación activado ✅");
      onDone?.();
    } catch (e) {
      console.error("[TOTP enroll] error:", e?.code, e?.message);

      const map = {
        "auth/invalid-verification-code": "Código incorrecto ❌",
        "auth/requires-recent-login":
          "Por seguridad, vuelve a iniciar sesión 🔒",
      };

      notify("error", map[e?.code] || e?.message || "No se pudo activar 2FA.");

      // si el código es incorrecto, limpia inputs
      if (e?.code === "auth/invalid-verification-code") {
        setDigits(["", "", "", "", "", ""]);
        setTimeout(() => inputsRef.current?.[0]?.focus?.(), 50);
      }
    } finally {
      setStepLoading(false);
    }
  }, [user, code, selectedApp, canSubmit, closeModal, onDone]);

  return (
    <>
      <button
        type="button"
        className="security-btn-primary twofa-activate-btn"
        onClick={openAndPrepare}
        disabled={loading}
      >
        {loading ? "Activando..." : "Activar autenticación"}
      </button>

      <Modal
        isOpen={isOpen}
        onRequestClose={stepLoading ? undefined : closeModal}
        style={modalStyles}
        shouldCloseOnOverlayClick={!stepLoading}
        shouldCloseOnEsc={!stepLoading}
      >
        {/* Header */}
        <div style={{ padding: "22px 22px 10px" }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "999px",
              background: "rgba(99, 102, 241, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
            }}
          >
            <Lock size={20} color="#4f46e5" />
          </div>

          <h2
            style={{
              margin: 0,
              textAlign: "center",
              fontSize: 20,
              fontWeight: 400,
              color: "#0f172a",
            }}
          >
            Activar verificación en dos pasos
          </h2>

          <p
            style={{
              margin: "10px 0 0",
              textAlign: "left",
              color: "#64748b",
              fontSize: 13.5,
              lineHeight: 1.4,
            }}
          >
            Elige tu app de autenticación
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "14px 22px 0" }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#334155",
              marginBottom: 10,
            }}
          >
            Elegir app de autenticación
          </div>

          {/* Radios tipo “card” */}
          <div style={{ display: "grid", gap: 10 }}>
            <label
              style={{
                border:
                  selectedApp === "Google Authenticator"
                    ? "2px solid var(--accent, #3B82F6)"
                    : "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: stepLoading ? "not-allowed" : "pointer",
                background:
                  selectedApp === "Google Authenticator" ? "#f8fafc" : "#fff",
              }}
            >
              <input
                type="radio"
                name="totpApp"
                value="Google Authenticator"
                checked={selectedApp === "Google Authenticator"}
                onChange={() => setSelectedApp("Google Authenticator")}
                disabled={stepLoading}
                style={{ accentColor: "var(--accent, #3B82F6)" }}
              />
              <span style={{ fontSize: 14, color: "#0f172a" }}>
                Google Authenticator
              </span>
            </label>

            <label
              style={{
                border:
                  selectedApp === "Authy"
                    ? "2px solid var(--accent, #3B82F6)"
                    : "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: stepLoading ? "not-allowed" : "pointer",
                background: selectedApp === "Authy" ? "#f8fafc" : "#fff",
              }}
            >
              <input
                type="radio"
                name="totpApp"
                value="Authy"
                checked={selectedApp === "Authy"}
                onChange={() => setSelectedApp("Authy")}
                disabled={stepLoading}
                style={{ accentColor: "var(--accent, #3B82F6)" }}
              />
              <span style={{ fontSize: 14, color: "#0f172a" }}>Authy</span>
            </label>
          </div>

          {/* QR */}
          <div
            style={{
              marginTop: 18,
              textAlign: "center",
              color: "#334155",
              fontSize: 13.5,
              fontWeight: 500,
            }}
          >
            Abre tu app de autenticación y escanea el código.
          </div>

          <div
            style={{
              marginTop: 10,
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              padding: 14,
              background: "#fff",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="QR"
                style={{
                  width: 240,
                  height: 240,
                  borderRadius: 12,
                  background: "#fff",
                }}
              />
            ) : (
              <div style={{ padding: 40, color: "#94a3b8" }}>
                Generando QR...
              </div>
            )}
          </div>

          {/* Manual key */}
          <button
            type="button"
            onClick={() => setShowManual((s) => !s)}
            disabled={stepLoading}
            style={{
              width: "100%",
              marginTop: 10,
              border: "none",
              background: "transparent",
              color: "#4f46e5",
              fontWeight: 600,
              cursor: stepLoading ? "not-allowed" : "pointer",
              padding: "8px 0",
            }}
          >
            ¿No puedes escanear? Usar clave manual{" "}
            <span
              style={{
                display: "inline-block",
                transform: showManual ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              ▾
            </span>
          </button>

          {showManual && (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 12,
                background: "#f8fafc",
                fontFamily: "Inter, sans-serif",
                fontSize: 12.5,
                color: "#0f172a",
                wordBreak: "break-all",
              }}
            >
              {manualKey || "—"}
            </div>
          )}

          {/* Code */}
          <div
            style={{
              marginTop: 14,
              textAlign: "center",
              color: "#334155",
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            Ingresa el código de 6 dígitos
          </div>

          <div
            onPaste={handlePaste}
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: 10,
              paddingBottom: 6,
            }}
          >
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => (inputsRef.current[idx] = el)}
                value={d}
                onChange={(e) => handleDigitChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                disabled={stepLoading}
                style={{
                  width: 46,
                  height: 52,
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  textAlign: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0f172a",
                  outline: "none",
                  background: "#fff",
                }}
              />
            ))}
          </div>

          <div
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 12.5,
              marginTop: 4,
              paddingBottom: 12,
              fontFamily: "Inter, sans-serif",
            }}
          >
            El código cambia cada 30 segundos.
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 22px 22px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            borderTop: "1px solid #f1f5f9",
            background: "#fff",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <button
            type="button"
            onClick={closeModal}
            disabled={stepLoading}
            style={{
              height: 46,
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              background: "#fff",
              fontWeight: 600,
              color: "#0f172a",
              cursor: stepLoading ? "not-allowed" : "pointer",
            }}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmEnroll}
            disabled={!canSubmit || stepLoading}
            style={{
              height: 46,
              borderRadius: 14,
              border: "none",
              fontWeight: 600,
              color: "#fff",
              cursor: !canSubmit || stepLoading ? "not-allowed" : "pointer",
              background: !canSubmit || stepLoading ? "#cbd5e1" : "#6366f1",
            }}
          >
            {stepLoading ? "Activando..." : "Activar verificación"}
          </button>
        </div>
      </Modal>
    </>
  );
}
